import { createJSONStorage } from 'jotai/utils';

/** Matches jotai's SyncStringStorage — local copy because it is not re-exported from `jotai/utils`. */
type SyncStringStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, newValue: string) => void;
  removeItem: (key: string) => void;
};

/**
 * Called when the underlying Storage throws — quota exhaustion, Safari private
 * mode, a locked-down webview. The wrapper still swallows the throw (a failed
 * write must never break a render); this is the seam that lets a caller
 * SURFACE the failure instead of losing it. Must not throw itself.
 */
export type SafeStorageErrorHandler = (
  operation: 'get' | 'set' | 'remove',
  key: string,
  error: unknown
) => void;

const reportSafely = (
  onError: SafeStorageErrorHandler | undefined,
  operation: 'get' | 'set' | 'remove',
  key: string,
  error: unknown
): void => {
  if (onError === undefined) return;
  try {
    onError(operation, key, error);
  } catch {
    // A throwing reporter must not turn a swallowed storage failure into a
    // thrown one — that is the whole point of the safe wrapper.
  }
};

const noopStorage: SyncStringStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const wrapBrowserStorage = (
  getStorage: () => Storage,
  onError?: SafeStorageErrorHandler
): SyncStringStorage => {
  if (typeof window === 'undefined') {
    return noopStorage;
  }

  try {
    const storage = getStorage();
    return {
      getItem: (key: string) => {
        try {
          return storage.getItem(key);
        } catch (error) {
          reportSafely(onError, 'get', key, error);
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          storage.setItem(key, value);
        } catch (error) {
          // Still ignored in restricted webviews — but no longer silent when
          // the caller passes a reporter.
          reportSafely(onError, 'set', key, error);
        }
      },
      removeItem: (key: string) => {
        try {
          storage.removeItem(key);
        } catch (error) {
          reportSafely(onError, 'remove', key, error);
        }
      },
    };
  } catch {
    return noopStorage;
  }
};

export const getSafeLocalStorage = (
  onError?: SafeStorageErrorHandler
): SyncStringStorage => wrapBrowserStorage(() => window.localStorage, onError);

export const getSafeSessionStorage = (
  onError?: SafeStorageErrorHandler
): SyncStringStorage => wrapBrowserStorage(() => window.sessionStorage, onError);

export const createSafeJotaiLocalStorage = <T>() =>
  createJSONStorage<T>(() => getSafeLocalStorage());

export const createSafeJotaiSessionStorage = <T>() =>
  createJSONStorage<T>(() => getSafeSessionStorage());
